import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  TextField,
  Avatar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  IconButton,
  Chip,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  InputAdornment,
  Alert,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Divider,
  Badge,
} from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory2";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VerifiedIcon from "@mui/icons-material/Verified";
import PaymentsIcon from "@mui/icons-material/Payments";
import ChecklistIcon from "@mui/icons-material/Checklist";
import Swal from "sweetalert2";
import { api, API_BASE } from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import ServiceForm from "./ServiceForm";
import ServiceFilterDrawer, { emptyFilters } from "./ServiceFilterDrawer";
import ServiceChecklistEditor from "./ServiceChecklistEditor";

const FREQ_LABEL = {
  one_time: "One Time",
  monthly: "Monthly",
  quarterly: "Quarterly",
  half_yearly: "Half Yearly",
  annual: "Annual",
};

const MONTH_SHORT = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun",
                     "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const formatSchedule = (s) => {
  const freq = s.frequency;
  const d = s.due_day;
  const m = s.due_month;
  if (!freq || freq === "one_time") return "—";
  if (freq === "monthly") return d ? `Day ${d}` : "—";
  if (!m || !d) return "—";
  const mi = Number(m);
  if (freq === "quarterly") {
    const months = [mi, ((mi + 2) % 12) + 1, ((mi + 5) % 12) + 1, ((mi + 8) % 12) + 1];
    return `${months.map((x) => MONTH_SHORT[x]).join("/")} · ${d}`;
  }
  if (freq === "half_yearly") {
    const second = ((mi + 5) % 12) + 1;
    return `${MONTH_SHORT[mi]}/${MONTH_SHORT[second]} · ${d}`;
  }
  if (freq === "annual") return `${MONTH_SHORT[mi]} ${d}`;
  return "—";
};

const toQuery = (page, pageSize, f) => {
  const q = new URLSearchParams({
    page,
    pageSize,
    search: f.search || "",
    frequency: (f.frequency || []).join(","),
    category: (f.category || []).join(","),
    audit: f.audit || "",
    status: f.status || "",
    price_min: f.price_min || "",
    price_max: f.price_max || "",
    sla_min: f.sla_min || "",
    sla_max: f.sla_max || "",
    has_checklist: f.has_checklist || "",
    sort_by: f.sort_by || "id",
    sort_order: f.sort_order || "desc",
  });
  return q.toString();
};

const countActive = (f) => {
  let c = 0;
  if (f.search) c++;
  if (f.frequency?.length) c++;
  if (f.category?.length) c++;
  if (f.audit) c++;
  if (f.status) c++;
  if (f.price_min) c++;
  if (f.price_max) c++;
  if (f.sla_min) c++;
  if (f.sla_max) c++;
  if (f.has_checklist) c++;
  return c;
};

const activeChips = (f, onRemove) => {
  const chips = [];
  const add = (key, label, value) => chips.push({ key, label: `${label}: ${value}` });

  if (f.search) add("search", "Name", f.search);
  if (f.frequency?.length) {
    add("frequency", "Freq", f.frequency.map((x) => FREQ_LABEL[x] || x).join(", "));
  }
  if (f.category?.length) add("category", "Category", f.category.join(", "));
  if (f.audit) add("audit", "Audit", f.audit === "1" ? "Yes" : "No");
  if (f.status) add("status", "Status", f.status === "1" ? "Active" : "Inactive");
  if (f.price_min) add("price_min", "Price ≥", f.price_min);
  if (f.price_max) add("price_max", "Price ≤", f.price_max);
  if (f.sla_min) add("sla_min", "SLA ≥", f.sla_min);
  if (f.sla_max) add("sla_max", "SLA ≤", f.sla_max);
  if (f.has_checklist) add("has_checklist", "Checklist", f.has_checklist);

  return chips.map((c) => (
    <Chip key={c.key} label={c.label} size="small" onDelete={() => onRemove(c.key)} />
  ));
};

const StatCard = ({ label, value, icon, color, loading }) => (
  <Card sx={{ height: "100%" }}>
    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Avatar sx={{ bgcolor: `${color}.main`, width: 40, height: 40 }}>{icon}</Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          {loading ? (
            <Skeleton width={60} height={28} />
          ) : (
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {value ?? 0}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

export default function Services() {
  const { can } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const [openAdd, setOpenAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [checklistItem, setChecklistItem] = useState(null);

  const [quickSearch, setQuickSearch] = useState("");
  const [filters, setFilters] = useState(emptyFilters);

  const [stats, setStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalPages: 0,
    totalRecords: 0,
  });

  const loadServices = useCallback(async (pageNo, pageSize, f) => {
    setLoading(true);
    try {
      const res = await api.get(`/services/list.php?${toQuery(pageNo, pageSize, f)}`);
      setServices(res.services || []);
      setPagination(res.pagination || { page: pageNo, pageSize, totalPages: 0, totalRecords: 0 });
    } catch (e) {
      setMsg({ type: "error", text: e.message || "Failed to load services" });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await api.get("/services/stats.php");
      setStats(res.stats || {});
    } catch (e) {
      // non-fatal
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadServices(1, 10, emptyFilters);
    loadStats();
  }, [loadServices, loadStats]);

  const handleQuickSearch = () => {
    const next = { ...filters, search: quickSearch };
    setFilters(next);
    loadServices(1, pagination.pageSize, next);
  };

  const handleApplyFilters = (next) => {
    setFilters(next);
    setQuickSearch(next.search || "");
    loadServices(1, pagination.pageSize, next);
  };

  const handleRemoveChip = (key) => {
    const next = { ...filters };
    if (Array.isArray(next[key])) next[key] = [];
    else next[key] = "";
    if (key === "search") setQuickSearch("");
    setFilters(next);
    loadServices(1, pagination.pageSize, next);
  };

  const handleClearAll = () => {
    setFilters(emptyFilters);
    setQuickSearch("");
    loadServices(1, pagination.pageSize, emptyFilters);
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("token");
      const url = `${API_BASE}/services/export.php?${toQuery(1, 1, filters)}`;
      const res = await fetch(url, { headers: { Authorization: "Bearer " + token } });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `services_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Export Failed", text: err.message });
    }
  };

  const handleDelete = async (service) => {
    const result = await Swal.fire({
      title: "Delete service?",
      text: `Are you sure you want to delete "${service.name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d32f2f",
      confirmButtonText: "Yes, delete",
    });
    if (!result.isConfirmed) return;
    try {
      await api.get(`/services/delete.php?id=${service.id}`);
      setMsg({ type: "success", text: "Service deleted" });
      loadServices(pagination.page, pagination.pageSize, filters);
      loadStats();
    } catch (e) {
      setMsg({ type: "error", text: e.message || "Delete failed" });
    }
  };

  const activeCount = countActive(filters);
  const chips = activeChips(filters, handleRemoveChip);

  return (
    <Box>
      {/* HEADER */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48 }}>
            <InventoryIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Services
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage all services, pricing and configuration
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1}>
          {can("services.view") && (
            <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExport}>
              Export
            </Button>
          )}
          {can("services.create") && (
            <Button
              variant="contained"
              startIcon={<AddCircleIcon />}
              onClick={() => setOpenAdd(true)}
            >
              Add Service
            </Button>
          )}
        </Stack>
      </Stack>

      {msg && (
        <Alert severity={msg.type} sx={{ mb: 2 }} onClose={() => setMsg(null)}>
          {msg.text}
        </Alert>
      )}

      {/* STATS STRIP */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Total Services"
            value={stats.total}
            icon={<InventoryIcon />}
            color="primary"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Active"
            value={stats.active}
            icon={<CheckCircleIcon />}
            color="success"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Audit Required"
            value={stats.audit_required}
            icon={<VerifiedIcon />}
            color="warning"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Avg Price"
            value={stats.avg_price != null ? `₹${stats.avg_price}` : "₹0"}
            icon={<PaymentsIcon />}
            color="info"
            loading={statsLoading}
          />
        </Grid>
      </Grid>

      {/* SEARCH + FILTER BAR */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="center">
          <TextField
            size="small"
            placeholder="Quick search by name"
            value={quickSearch}
            onChange={(e) => setQuickSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleQuickSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 240 }}
          />
          <Button variant="contained" onClick={handleQuickSearch} startIcon={<SearchIcon />}>
            Search
          </Button>
          <Badge badgeContent={activeCount} color="primary">
            <Button
              variant="outlined"
              startIcon={<FilterAltIcon />}
              onClick={() => setDrawerOpen(true)}
            >
              Filters
            </Button>
          </Badge>
        </Stack>

        {chips.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 1.5, flexWrap: "wrap", gap: 1 }}
            alignItems="center"
          >
            {chips}
            <Button size="small" onClick={handleClearAll}>
              Clear All
            </Button>
          </Stack>
        )}
      </Paper>

      {/* TABLE */}
      <Paper>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Frequency</TableCell>
                <TableCell>Schedule</TableCell>
                <TableCell>SLA</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Checklist</TableCell>
                <TableCell>Audit</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading &&
                [...Array(5)].map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    <TableCell colSpan={11}>
                      <Skeleton height={36} />
                    </TableCell>
                  </TableRow>
                ))}

              {!loading && services.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                    <Typography variant="h3" sx={{ mb: 1 }}>📁</Typography>
                    <Typography variant="body2" color="text.secondary">
                      No services found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                services.map((s, idx) => (
                  <TableRow key={s.id} hover>
                    <TableCell>
                      {(pagination.page - 1) * pagination.pageSize + idx + 1}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{s.name}</TableCell>
                    <TableCell>
                      <Typography variant="caption">{s.service_type}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {FREQ_LABEL[s.frequency] || s.frequency}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {formatSchedule(s)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{s.sla_days}d</Typography>
                    </TableCell>
                    <TableCell>₹{s.default_price}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={s.checklist_count || 0}
                        color={s.checklist_count > 0 ? "primary" : "default"}
                        variant={s.checklist_count > 0 ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={s.audit_required == 1 ? "Yes" : "No"}
                        color={s.audit_required == 1 ? "warning" : "default"}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={s.is_active == 1 ? "Active" : "Inactive"}
                        color={s.is_active == 1 ? "success" : "error"}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {can("services.checklist") && (
                        <Tooltip title="Manage Checklist">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => setChecklistItem(s)}
                          >
                            <ChecklistIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {can("services.edit") && (
                        <Tooltip title="Edit">
                          <IconButton size="small" color="primary" onClick={() => setEditItem(s)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {can("services.delete") && (
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDelete(s)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Box>

        <Divider />

        <TablePagination
          component="div"
          count={pagination.totalRecords || 0}
          page={Math.max(0, (pagination.page || 1) - 1)}
          rowsPerPage={pagination.pageSize}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(_, p) => loadServices(p + 1, pagination.pageSize, filters)}
          onRowsPerPageChange={(e) => loadServices(1, parseInt(e.target.value, 10), filters)}
        />
      </Paper>

      {/* FILTER DRAWER */}
      <ServiceFilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        filters={filters}
        onApply={handleApplyFilters}
      />

      {/* ADD DIALOG */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} fullWidth maxWidth="md">
        <DialogTitle>Add Service</DialogTitle>
        <DialogContent dividers>
          <ServiceForm
            onSuccess={() => {
              loadServices(pagination.page, pagination.pageSize, filters);
              loadStats();
            }}
            onClose={() => setOpenAdd(false)}
          />
        </DialogContent>
      </Dialog>

      {/* EDIT DIALOG */}
      <Dialog open={!!editItem} onClose={() => setEditItem(null)} fullWidth maxWidth="md">
        <DialogTitle>Edit Service</DialogTitle>
        <DialogContent dividers>
          {editItem && (
            <ServiceForm
              data={editItem}
              onSuccess={() => {
                loadServices(pagination.page, pagination.pageSize, filters);
                loadStats();
              }}
              onClose={() => setEditItem(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* CHECKLIST DIALOG */}
      <Dialog
        open={!!checklistItem}
        onClose={() => setChecklistItem(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Manage Checklist</DialogTitle>
        <DialogContent dividers>
          {checklistItem && (
            <ServiceChecklistEditor
              serviceId={checklistItem.id}
              serviceName={checklistItem.name}
              onClose={() => setChecklistItem(null)}
              onSaved={() => {
                loadServices(pagination.page, pagination.pageSize, filters);
                loadStats();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
